// submitStreamPayment.js
'use strict';
import { v4 as uuidv4 } from 'uuid';

import getMeData from '../selector/getMeData.js';
import getOperationData from '../selector/getOperationData.js';
import getLiveStreamPrice from '../selector/getLiveStreamPrice.js';
import getNetworkingData from '../selector/getNetworkingData.js';

import setStreamToken from '../action/setStreamToken.js';
import setPrivateStreamAutoPayment from '../action/setPrivateStreamAutoPayment.js';
import submitLivestreamPaymentTracking from '../action/submitLivestreamPaymentTracking.js';

import fetch from '../resource/customFetch.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import {
  PRESET_SD,
  CHARGE_PERIOD_SECONDS,
  livestreamPaymentVia as livestreamPaymentViaConstant,
  LivestreamTokenVia,
} from '../resource/liveStreamConstants.js';
import { EventTypes } from '../resource/mixpanel.js';

import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

/**
 * Submit stream payment
 * @kind action
 * @param {string} {streamId} - stream id.
 * @param {string} {sessionId} - session id.
 * @param {'sd'|'preview'} {[preset = 'sd']} - stream preset.
 * @param {string} {via} - submit payment via action.
 * @return {Promise} Action promise.
 */
const submitStreamPayment =
  ({
    streamId,
    sessionId,
    preset = PRESET_SD,
    via = livestreamPaymentViaConstant.AUTO,
  }) =>
  async (dispatch, getState) => {
    const activeStreamId = getOperationData(
      getState(),
      ['liveStream'],
      'activeStreamId'
    );
    const token = getMeData(getState(), 'token');

    const isFetching = getNetworkingData(
      getState(),
      ['submit', streamId, sessionId, 'payment'],
      'isFetching'
    );

    if (
      !streamId ||
      !activeStreamId ||
      !token ||
      streamId !== activeStreamId ||
      isFetching
    ) {
      return dispatch({ type: '' });
    }

    if (document?.hidden) {
      return dispatch({ type: '' });
    }

    const price = getLiveStreamPrice(getState(), streamId, preset);

    const isError = !!getNetworkingData(
      getState(),
      ['liveStream', 'manifest', streamId],
      'error'
    );
    if (isError) {
      return dispatch({ type: '' });
    }

    const requestId = uuidv4();
    const paymentTrackingPayload = {
      streamId,
      sessionId,
      requestId,
      via,
      price,
      duration: CHARGE_PERIOD_SECONDS,
    };

    let networkingSelectPath;
    let fetchOptions;
    let url;

    try {
      networkingSelectPath = ['submit', streamId, sessionId, 'payment'];
      fetchOptions = {
        method: 'POST',
        headers: {
          ...getHeaders(),
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Request-ID': requestId,
        },
        body: JSON.stringify({
          duration: CHARGE_PERIOD_SECONDS,
        }),
      };
      url = getResourceUrl({ endpoint: `/streams/${streamId}/pay` });
    } catch (error) {
      dispatch(
        submitLivestreamPaymentTracking({
          trackingType: EventTypes.LIVESTREAM_PAYMENT_CANCELED,
          ...paymentTrackingPayload,
          error,
        })
      );
    }

    dispatch(
      submitLivestreamPaymentTracking({
        trackingType: EventTypes.LIVESTREAM_PAYMENT,
        ...paymentTrackingPayload,
      })
    );
    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }
      const data = await response.json();
      if (data?.token || data?.byteplus_token) {
        dispatch(
          setStreamToken({
            streamId,
            token: data.token,
            byteplusToken: data.byteplus_token,
            byteplusTokenNbf: data.nbf,
            byteplusTokenExp: data.exp,
            via: LivestreamTokenVia.PAYMENT,
          })
        );
      }

      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      dispatch(setPrivateStreamAutoPayment({ streamId, isEnabled: false }));
      dispatch(
        submitLivestreamPaymentTracking({
          trackingType: EventTypes.LIVESTREAM_PAYMENT_ERROR,
          ...paymentTrackingPayload,
          error,
        })
      );
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

// TODO: change to submitLiveStreamPayment
export default submitStreamPayment;
